import React from 'react';
import { Route, IndexRoute, Redirect } from 'react-router';
import { datadogRum } from '@datadog/browser-rum';

// actions
import {
  cleanupEditScriptUi,
  initAdvancedUi,
  initCreateScriptUi,
  initEditScriptUi,
  initManageScriptsUi,
  initManualScriptUi,
  initExperimentScriptUi
} from 'current/uiwidgets/screen-transition';
import advancedScriptFlowDefinition from 'advanced/scriptFlowDefinition';
import manualScriptFlowDefinition from 'manual/scriptFlowDefinition';
import experimentScriptFlowDefinition from 'experiment/scriptFlowDefinition';
import { startScriptFlow } from 'script-flow';

// selectors
import { isScriptFlowActive } from 'script-flow/selectors';

// Components
import ManageContainer from 'manage/ManageContainer';
import ScriptCreatorContainer from 'edit/ScriptCreatorContainer';
import CreateContainer from 'create/CreateContainer';
import AdvancedScriptContainer from 'advanced/AdvancedScriptContainer';
import ManualScriptContainer from 'manual/ManualScriptContainer';
import TrialScriptContainer from 'experiment/TrialScriptContainer';
import RedirectToDefaultContainer from 'routes/RedirectToDefaultContainer';
import { getHasDoneFirstLoad } from 'settings/selectors';
import { isWebPerformancePlatformBetaUser } from 'user/selectors';
import BulkFieldViewSeedScripts from 'create/bulk/FVSS/BulkFieldViewSeedScripts';
import FieldLevelSeedRecs from 'create/bulk/recommendations/FieldLevelSeedRecs';

const onEnterCreate = ({ dispatch }) => {
  // They can deeplink to create
  dispatch(initCreateScriptUi());
};

export const onEnterAdvanced = ({ dispatch, state, store }) => {
  // Handle case where user browsed directly to page without starting script flow.
  if (!isScriptFlowActive(state)) {
    const unsubscribe = store.subscribe(() => {
      // wait untill initial app load is done before requesting more info
      if (getHasDoneFirstLoad(store.getState()) === true) {
        unsubscribe();
        dispatch(startScriptFlow({ definition: advancedScriptFlowDefinition })); // Sends to step 1, regardless of url.
      }
    });
  }
  dispatch(initAdvancedUi());
};

export const onEnterManual = ({ dispatch, next, state }) => {
  dispatch(initManualScriptUi(next.params.scriptType));

  if (!isScriptFlowActive(state)) {
    dispatch(startScriptFlow({ definition: manualScriptFlowDefinition }));
  }
};

const onEnterExperiment = ({ dispatch, next, state, store }) => {
  dispatch(initExperimentScriptUi(next.params.scriptType));

  if (!isScriptFlowActive(state)) {
    const unsubscribe = store.subscribe(() => {
      const state = store.getState();

      if (getHasDoneFirstLoad(state) === true) {
        unsubscribe();
        if (isWebPerformancePlatformBetaUser(state)) {
          dispatch(startScriptFlow({ definition: experimentScriptFlowDefinition, stepNumber: 1 }));
        } else {
          window.location = '/variable-rate/';
        }
      }
    });
  }
};

const onEnterManage = ({ dispatch }) => {
  // They can deeplink into manage
  dispatch(initManageScriptsUi());
};

const onEnterEdit = ({ next, dispatch }) => {
  dispatch(initEditScriptUi(next.params.prescription));
};

const onLeaveEdit = ({ previous, dispatch }) => {
  dispatch(cleanupEditScriptUi(previous.params.prescription));
};

export const routes = [
  'ADVANCED',
  'MANUAL',
  'CREATE',
  'EDIT',
  'MANAGE'
].reduce((result, key) => { result[key] = key; return result; }, {});

// This is straight JSX and not a full component, passed to react-router
const Router =  (store, defaultRoute = '/') => (
  <Route path={defaultRoute}>
    <IndexRoute
      component={RedirectToDefaultContainer}
    />
    <Route
      component={ManageContainer}
      exact
      onEnter={(next) => {
        datadogRum.startView({name: 'Landing View - Script Management'});
        onEnterManage({ next, dispatch: store.dispatch });
      }}
      path='manage/:client/:field(/:prescription)'
    />
    <Route
      component={CreateContainer}
      exact
      onEnter={(next) => {
        datadogRum.startView({name: 'Script Creation - Choose Script type'});
        onEnterCreate({ next, dispatch: store.dispatch });
      }}
      path='create/:client/:field'
    />
    <Route
      component={AdvancedScriptContainer}
      exact
      onEnter={(next) => {
        datadogRum.startView({ name: 'Advanced Script Creation' });
        onEnterAdvanced({ next, dispatch: store.dispatch, state: store.getState(), store });
      }}
      path='advanced/:scriptType/:step/:client/:field'
    />
    <Route
      component={ManualScriptContainer}
      exact
      onEnter={(next) => {
        datadogRum.startView({ name: 'Manual Script Creation' });
        onEnterManual({ next, dispatch: store.dispatch, state: store.getState() });
      }}
      path='manual/:scriptType/:step/:client/:field'
    />
    <Route
      component={TrialScriptContainer}
      exact
      onEnter={(next) =>
        onEnterExperiment({ next, dispatch: store.dispatch, state: store.getState(), store })}
      path='experiment/application/:step/:client/:field'
    />
    <Route
      component={ScriptCreatorContainer}
      exact
      onEnter={(next, replace) => {
        datadogRum.startView({name: 'Script Editing'});
        onEnterEdit({ next, replace, dispatch: store.dispatch, state: store.getState() });
      }}
      onLeave={(previous) =>
        onLeaveEdit({ previous, dispatch: store.dispatch, state: store.getState() })}
      path='edit/:client/:field/:prescription'
    />
    <Route
      component={BulkFieldViewSeedScripts}
      exact
      onEnter={() => {
        datadogRum.startView({name: 'Bulk FVSS' });
      }}
      // Let's define later on what it would be determined route by Product for this feature
      path='bulk-create'
    />
    <Route
      component={FieldLevelSeedRecs}
      exact
      onEnter={() => {
        datadogRum.startView({name: 'Field Level Seed Recs'});
      }}
      // Let's define later on what it would be determined route by Product for this feature
      path='recommendations'
    />
    <Redirect from='/*' to={defaultRoute} />
  </Route>
);

export default Router;
